import { useState, useEffect } from 'react'
import { Text, Primary, SvgIcon } from '@cinch-labs/design-system'
import { StoreWithDistance } from '../../hooks/types'
import styles from './store-details-card.module.css'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { formatOpeningHours } from '../../utils/format-opening-hours'
import { round } from 'lodash'
import { formatAddress } from '../../utils/store-card-details'

const METERS_TO_MILES_CONVERSION = 0.000_621_371
const toMiles = (meters: number) =>
  round(meters * METERS_TO_MILES_CONVERSION, 1)

interface StoreDetailsCardProps {
  store: StoreWithDistance
  isMinimized: boolean
  onExpand: () => void
  onSelectStore: () => void
}

export const StoreDetailsCard: React.FC<StoreDetailsCardProps> = ({
  store,
  isMinimized,
  onExpand,
  onSelectStore,
}) => {
  const { trackEvent } = useAnalytics()
  const [showMoreInfo, setShowMoreInfo] = useState(false)

  useEffect(() => {
    setShowMoreInfo(false)
  }, [store.store.id])

  const handleToggleInfo = () => {
    const newState = !showMoreInfo
    setShowMoreInfo(newState)

    trackEvent({
      eventName: `Toggle Store Info`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: newState ? 'see_more_info' : 'see_less_info',
          label: store.store.name,
          type: 'map_view',
        },
      },
    })
  }

  return (
    <div
      className={`${styles.card} ${isMinimized ? styles.minimized : ''} ${
        showMoreInfo ? styles.expanded : ''
      }`}
      onClick={isMinimized ? onExpand : undefined}
      style={{ cursor: isMinimized ? 'pointer' : 'default' }}
    >
      <div className={styles.cardContent}>
        {!isMinimized && store.distance !== 0 && (
          <Text fontSizeFixed="s" className={styles.distance}>
            {toMiles(store.distance)} miles away
          </Text>
        )}

        <Text
          fontSizeFixed={isMinimized ? 'm' : 'xl'}
          fontWeight="bold"
          className={styles.storeName}
        >
          {store.store.displayName}
        </Text>

        <div
          className={`${styles.additionalContent} ${
            isMinimized ? styles.hidden : ''
          }`}
        >
          <Text fontSizeFixed="s" className={styles.address}>
            {formatAddress(store.store)}
          </Text>

          {store.store.openingHours && (
            <>
              <button
                className={styles.toggleButton}
                onClick={handleToggleInfo}
              >
                <SvgIcon name="info" />
                <Text fontSizeFixed="s" className={styles.toggleText}>
                  {showMoreInfo ? 'See less info' : 'See more info'}
                </Text>
              </button>

              <div
                className={`${styles.scrollableSection} ${
                  showMoreInfo ? styles.visible : ''
                }`}
              >
                <div className={styles.expandedContent}>
                  <Text
                    fontSizeFixed="m"
                    fontWeight="bold"
                    className={styles.openingHoursTitle}
                  >
                    Opening hours
                  </Text>
                  <div className={styles.hoursContainer}>
                    {formatOpeningHours(store.store.openingHours).map(
                      ({ day, hours }) => (
                        <div key={day} className={styles.hoursRow}>
                          <Text fontSizeFixed="s">{day}</Text>
                          <Text fontSizeFixed="s">{hours}</Text>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className={`${styles.buttonContainer} ${
          isMinimized ? styles.hidden : ''
        }`}
      >
        <Primary
          onClick={() => {
            trackEvent({
              eventName: `Choose Store from Map`,
              type: TrackingEventTypes.ADOBE,
              data: {
                event: {
                  name: EventNames.click,
                  category: 'cinch_servicing',
                  action: 'choose_store_from_map',
                  label: store.store.name,
                },
              },
            })
            onSelectStore()
          }}
          size="large"
          classNames={{ button: styles.chooseButton }}
          aria-label={`Choose ${store.store.name}`}
        >
          Choose service centre
        </Primary>
      </div>
    </div>
  )
}
